import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from "react";
const queryValue = value => {
  const scalar = Array.isArray(value) ? value[0] : value;
  return scalar == null ? undefined : String(scalar);
};
const queriesMatch = (left = {}, right = {}) => {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every(key => queryValue(left[key]) === queryValue(right[key]));
};
export function createOptimisticMapQueryState(initialQuery = {}) {
  let currentQuery = initialQuery;
  let pendingQuery = null;
  return {
    setOptimisticQuery(nextQuery) {
      currentQuery = nextQuery;
      pendingQuery = nextQuery;
    },
    cancelOptimisticQuery(failedQuery, routerQuery) {
      if (pendingQuery === failedQuery) {
        pendingQuery = null;
        currentQuery = routerQuery;
      }
      return currentQuery;
    },
    syncRouterQuery(nextQuery) {
      if (pendingQuery && queriesMatch(nextQuery, pendingQuery)) {
        pendingQuery = null;
      }
      if (!pendingQuery) {
        currentQuery = nextQuery;
      }
      return currentQuery;
    }
  };
}

/**
 * Keeps map interaction state ahead of a shallow Next.js URL update without
 * allowing the previous router query to overwrite that local intent.
 */
export function useOptimisticMapQuery(t0) {
  const $ = _c(9);
  const {
    router,
    query
  } = t0;
  const queryRef = useRef(query);
  let t1;
  if ($[0] !== query) {
    t1 = createOptimisticMapQueryState(query);
    $[0] = query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const stateRef = useRef(t1);
  let t2;
  let t3;
  if ($[2] !== query) {
    t2 = () => {
      queryRef.current = stateRef.current.syncRouterQuery(query);
    };
    t3 = [query];
    $[2] = query;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== router) {
    t4 = async (nextQuery, t5) => {
      const method = t5 === undefined ? "replace" : t5;
      queryRef.current = nextQuery;
      stateRef.current.setOptimisticQuery(nextQuery);
      ;
      try {
        const completed = await router[method]({
          pathname: router.pathname,
          query: nextQuery
        }, undefined, {
          shallow: true
        });
        if (completed === false) {
          queryRef.current = stateRef.current.cancelOptimisticQuery(nextQuery, router.query);
        }
        return completed;
      } catch (t6) {
        const error = t6;
        queryRef.current = stateRef.current.cancelOptimisticQuery(nextQuery, router.query);
        if (!error?.cancelled) {
          throw error;
        }
        return false;
      }
    };
    $[5] = router;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const updateMapQuery = t4;
  let t5;
  if ($[7] !== updateMapQuery) {
    t5 = {
      queryRef,
      updateMapQuery
    };
    $[7] = updateMapQuery;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}